import React from 'react';
import {
  ArrowLeft,
  Bot,
  Zap,
  Activity,
  Bug,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle
} from 'lucide-react';

const V2ChatHeader = ({
  workflowName,
  executionStatus,
  runId,
  onBack,
  onDebug,
  onToggleEventLog,
  showEventLog,
  isAdmin = false
}) => {

  const getStatusIcon = () => {
    switch (executionStatus) {
      case 'ready':
        return <Play className="w-4 h-4" />;
      case 'running':
        return <Activity className="w-4 h-4 animate-pulse" />;
      case 'awaiting_user':
        return <Clock className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'failed':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getStatusColor = () => {
    switch (executionStatus) {
      case 'ready':
        return 'text-blue-500';
      case 'running':
        return 'text-green-500';
      case 'awaiting_user':
        return 'text-yellow-500';
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusText = () => {
    switch (executionStatus) {
      case 'ready':
        return 'Ready to start';
      case 'running':
        return 'Executing workflow';
      case 'awaiting_user':
        return 'Waiting for response';
      case 'completed':
        return 'Workflow completed';
      case 'failed':
        return 'Execution failed';
      default:
        return 'Unknown status';
    }
  };


  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left section - Navigation and title */}
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Back to Workflows"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center space-x-3">
            {/* Workflow icon */}
            <div className="p-3 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl shadow-lg">
              <Bot className="w-6 h-6 text-white" />
            </div>

            {/* Workflow info */}
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {workflowName}
              </h1>
              <div className="flex items-center space-x-3 text-sm">
                {/* Execution status */}
                <div className={`flex items-center space-x-1 ${getStatusColor()}`}>
                  {getStatusIcon()}
                  <span>{getStatusText()}</span>
                </div>

                {/* Run ID */}
                {runId && (
                  <div className="text-gray-500 dark:text-gray-400">
                    <span className="text-xs">Run: {runId.slice(0, 8)}...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right section - Actions */}
        <div className="flex items-center space-x-2">
          {/* Event log toggle */}
          <button
            onClick={onToggleEventLog}
            className={`p-2 rounded-lg transition-colors ${
              showEventLog
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title={showEventLog ? 'Hide Events' : 'Show Events'}
          >
            <Activity className="w-5 h-5" />
          </button>

          {/* Debug button - Admin only */}
          {isAdmin && runId && (
            <button
              onClick={onDebug}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Debug Execution"
            >
              <Bug className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Status bar for additional info */}
      {(executionStatus === 'running' || executionStatus === 'awaiting_user') && (
        <div className="mt-3 px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2">
              <Zap className="w-4 h-4 text-blue-500" />
              <span className="text-blue-700 dark:text-blue-300">
                {executionStatus === 'running'
                  ? 'Workflow is executing in real-time...'
                  : 'Workflow is waiting for your response...'
                }
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default V2ChatHeader;